import React, { useState, useRef } from 'react';
import { Box, Button, Menu, MenuItem, Modal } from '@mui/material';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ReactWebcam from 'react-webcam';
import ImageCropper from '../../../components/imageCropper';

export default function AgentProfilePicture({
  userId,
  agentDetails,
  updateAgentProfilePicture,
  isEditable = true,
  uploadedImage,
  preview,
}) {
  const [selectedImage, setSelectedImage] = useState(null);
  const [isCropping, setIsCropping] = useState(false);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showWebcam, setShowWebcam] = useState(false);
  const webcamRef = useRef(null);
  const open = Boolean(anchorEl);

  const handleImageSelect = (file) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setSelectedImage(reader.result);
        setIsCropping(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropSave = (croppedResult) => {
    updateAgentProfilePicture(croppedResult);
    setIsCropping(false);
  };

  const handleCameraCapture = () => {
    setShowWebcam(true);
    setAnchorEl(null);
  };

  const handleCapturePhoto = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    setSelectedImage(imageSrc);
    setIsCropping(true);
    setShowWebcam(false);
  };

  const handleGalleryUpload = () => {
    if (galleryInputRef.current) {
      galleryInputRef.current.click();
    }
    setAnchorEl(null);
  };

  const handleEditClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Box className="profile-photo" sx={{ position: 'relative' }}>
        <img
          src={
            uploadedImage
              ? URL.createObjectURL(uploadedImage)
              : agentDetails?.profile_picture
          }
          alt="Profile"
          style={{
            borderRadius: '50%',
            width: '145px',
            height: '145px',
            border: '4px solid white',
            objectFit: 'cover',
          }}
        />
        {agentDetails?.customer_id === userId && isEditable && (
          <Box className="edit-button">
            <Button
              onClick={handleEditClick}
              sx={{
                minWidth: 'auto',
                padding: '4px',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                borderRadius: '50%',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                },
              }}
            >
              <DriveFileRenameOutlineIcon className="edit-icon" fontSize="small" />
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
              <MenuItem onClick={handleCameraCapture}>
                <CameraAltIcon fontSize="small" sx={{ mr: 1 }} />
                Capture Photo
              </MenuItem>
              <MenuItem onClick={handleGalleryUpload}>
                <PhotoLibraryIcon fontSize="small" sx={{ mr: 1 }} />
                Upload from Gallery
              </MenuItem>
            </Menu>
            <input
              ref={cameraInputRef}
              type="file"
              hidden
              accept="image/*"
              capture="user"
              onChange={(e) => handleImageSelect(e.target.files[0])}
            />
            <input
              ref={galleryInputRef}
              type="file"
              hidden
              accept="image/png, image/jpeg, image/jpg"
              onChange={(e) => handleImageSelect(e.target.files[0])}
            />
          </Box>
        )}
      </Box>

      <Modal open={showWebcam} onClose={() => setShowWebcam(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            padding: 2,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <ReactWebcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            width={300}
          />
          <Button onClick={handleCapturePhoto} variant="contained" sx={{ mt: 2 }}>
            Capture
          </Button>
        </Box>
      </Modal>

      <ImageCropper
        open={isCropping && { image: selectedImage }}
        onClose={() => setIsCropping(false)}
        onCropSave={handleCropSave}
        aspect={1}
      />
    </>
  );
}
